/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Node Component
 * Renders individual topology nodes as SVG elements with type-specific shapes
 */

import React from "react";
import { TopologyNode, NodePosition } from "./types";
import { NODE_TYPE_CONFIG, STATUS_COLORS } from "./constants";

interface DasNodeProps {
  node: TopologyNode;
  position: NodePosition;
  isHovered: boolean;
  isSelected: boolean;
  isCollapsed: boolean;
  showLabel: boolean;
  hasChildren: boolean;
  hcmLedColor?: string;
  onMouseEnter: (nodeId: string) => void;
  onMouseLeave: () => void;
  onClick: (nodeId: string) => void;
  onToggleCollapse: (nodeId: string) => void;
}

export const DasNode: React.FC<DasNodeProps> = ({
  node,
  position,
  isHovered,
  isSelected,
  isCollapsed,
  showLabel,
  hasChildren,
  hcmLedColor,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onToggleCollapse,
}) => {
  const config = NODE_TYPE_CONFIG[node.type];
  const statusColor = STATUS_COLORS[node.status];
  
  const { x, y } = position;
  const { width, height, shape, radius } = config;

  const isDeuLabel = node.label?.startsWith("DEU -");
  const isDxruLabel = node.label?.startsWith("DxRU");
  const deuScale = 0.8;
  const nodeWidth = isDeuLabel ? Math.max(Math.round(96 * deuScale), 60) : isDxruLabel ? Math.max(width, 95) : width;
  const nodeHeight = isDeuLabel ? Math.max(Math.round(height * deuScale), 12) : height;
  const nodeRadius = isDeuLabel ? Math.max(Math.round((radius || 4) * deuScale), 2) : radius;

  // Mockup uses consistent slate colors for all nodes
  const fill = "#334155";  // slate-700
  const stroke = "#64748b"; // slate-500

  // Apply glow effect on hover or selection
  const strokeWidth = isSelected ? 1.5 : isHovered ? 1.5 : 1.5;
  const opacity = isHovered || isSelected ? 1 : 0.9;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(node.id);
  };

  const handleCollapseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCollapse(node.id);
  };

  // Calculate centered positions (mockup style: nodes centered at transform origin)
  const rectX = -nodeWidth / 2;
  const rectY = -nodeHeight / 2;
  const isCircle = shape === "circle";
  const r = radius || nodeWidth / 2;
  const insideChipLabel = node.label || node.equipmentType || node.type;

  return (
    <g
      transform={`translate(${x},${y})`}
      className="das-node"
      onMouseEnter={() => onMouseEnter(node.id)}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
    >
      {/* Hover/selection highlight (mockup style) */}
      {(isHovered || isSelected) && (
        <circle
          r={isSelected ? 30 : 26}
          fill={isSelected ? "rgba(59, 130, 246, 0.22)" : "rgba(241, 245, 249, 0.18)"}
        />
      )}

      {/* Node shape */}
      {shape === "rect" && (
        <rect
          x={rectX}
          y={rectY}
          width={nodeWidth}
          height={nodeHeight}
          rx={nodeRadius}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      )}
      
      {shape === "roundedRect" && (
        <rect
          x={rectX}
          y={rectY}
          width={nodeWidth}
          height={nodeHeight}
          rx={nodeRadius}
          ry={nodeRadius}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      )}
      
      {shape === "circle" && (
        <circle
          r={r}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      )}

      {/* Status indicator dot (mockup style - smaller and positioned differently) */}
      {/* For MA3000 (site type), show HCM status LED, otherwise show device status */}
      <circle
        cx={isCircle ? r - 2 : nodeWidth / 2 - 4}
        cy={isCircle ? -r + 2 : -nodeHeight / 2 + 4}
        r={3.5}
        fill={node.type === 'site' && hcmLedColor ? hcmLedColor : statusColor}
        stroke="#0f172a"
        strokeWidth={1}
      />
      
      {/* Glow effect for HCM LED on MA3000 */}
      {node.type === 'site' && hcmLedColor && (
        <circle
          cx={isCircle ? r - 2 : nodeWidth / 2 - 4}
          cy={isCircle ? -r + 2 : -nodeHeight / 2 + 4}
          r={5.5}
          fill={hcmLedColor}
          opacity={0.3}
        />
      )}

      {/* Equipment type text inside the box */}
      <text
        x={0}
        y={isCircle ? 0 : (isDeuLabel ? 0.5 : 1)}
        fontSize={isCircle ? 6 : 8}
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fill="#e2e8f0"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ userSelect: "none", pointerEvents: "none" }}
      >
        {insideChipLabel}
      </text>

      {/* Node label (mockup style - monospace font) */}
      {showLabel && (() => {
        // Show device name from metadata (hostname/serial only, empty if neither exists)
        let deviceName = '';
        if (node.equipmentType === 'OCH' && node.metadata?.deviceName) {
          deviceName = node.metadata.deviceName;
        } else {
          deviceName = node.metadata?.equipment?.hostname || node.serialNumber || '';
        }

        if (!deviceName && !isCollapsed) return null;
        
        return (
          <text
            x={isCircle ? r + 4 : nodeWidth / 2 + 6}
            y={4}
            fontSize={6.7}
            fontFamily="ui-monospace, monospace"
            fill={isHovered ? "#ffffff" : "#94a3b8"}
            fontWeight={isHovered ? "700" : "400"}
            style={{ userSelect: "none", pointerEvents: "none" }}
          >
            {deviceName}{isCollapsed ? " ▶" : ""}
          </text>
        );
      })()}

      {/* Collapse/expand indicator (mockup style) */}
      {hasChildren && (
        <g
          transform={`translate(${isCircle ? r + 8 : nodeWidth / 2 + 8}, ${-nodeHeight / 2 - 8})`}
          onClick={handleCollapseClick}
          style={{ cursor: "pointer" }}
          className="collapse-indicator"
        >
          {isCollapsed ? (
            <>
              <circle r={8} fill="#475569" stroke="#64748b" strokeWidth={1} />
              <text
                x={0}
                y={3.5}
                fontSize={11}
                fill="#fff"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                fontWeight="700"
              >
                +
              </text>
            </>
          ) : (
            isHovered && (
              <>
                <circle r={8} fill="#334155" stroke="#475569" strokeWidth={1} opacity={0.9} />
                <text
                  x={0}
                  y={3.5}
                  fontSize={10}
                  fill="#94a3b8"
                  textAnchor="middle"
                  fontFamily="monospace"
                >
                  −
                </text>
              </>
            )
          )}
        </g>
      )}
    </g>
  );
};

/**
 * SVG Filters definition for glow effects
 */
export const NodeFilters: React.FC = () => (
  <defs>
    <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);
